import React, { useState, useEffect } from 'react';
import { TextField, Button, MenuItem, Grid, CircularProgress, Alert, InputLabel, Select, FormControl, Snackbar } from '@mui/material';
import { FormContainer } from '../ui/StyledComponents';
import supabase from '../../supabaseClient';
import { shiftOptions } from '../../constants/shiftData';

const initialState = {
  name: '',
  description: '',
  teacher_id: '',
  start_date: '',
  shift: '',
};

function ManageClassForm({ classId, onSuccess }) {
  const [form, setForm] = useState(initialState);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    const fetchTeachers = async () => {
      const { data, error } = await supabase.from('teachers').select('id, first_name, last_name');
      if (!error) setTeachers(data || []);
    };
    fetchTeachers();
  }, []);

  useEffect(() => {
    const fetchClassDetails = async () => {
      if (!classId) return;
      
      setLoading(true);
      const { data, error } = await supabase
        .from('classes')
        .select('*')
        .eq('id', classId)
        .single();

      if (error) {
        setError('Error fetching class details');
        console.error('Error:', error);
      } else if (data) {
        setForm({
          name: data.name || '',
          description: data.description || '',
          teacher_id: data.teacher_id || '',
          start_date: data.start_date || '',
          shift: data.shift || '',
        });
      }
      setLoading(false);
    };

    fetchClassDetails();
  }, [classId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!form.name || !form.teacher_id) {
      setError('Class name and teacher are required.');
      setLoading(false);
      return;
    }

    const { error } = await supabase
      .from('classes')
      .update({
        name: form.name,
        description: form.description,
        teacher_id: form.teacher_id,
        start_date: form.start_date || null,
        shift: form.shift || null,
      })
      .eq('id', classId);

    if (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } else {
      setSnackbarMessage('Class updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Navigate after a short delay
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 1000);
    }
    setLoading(false);
  };

  if (loading && !form.name) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <FormContainer>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Class Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              name="description"
              value={form.description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Teacher</InputLabel>
                  <Select
                    name="teacher_id"
                    value={form.teacher_id}
                    onChange={handleChange}
                    label="Teacher"
                  >
                    {teachers.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.first_name} {t.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  name="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Shift</InputLabel>
              <Select
                name="shift"
                value={form.shift}
                onChange={handleChange}
                label="Shift"
              >
                {shiftOptions.map((shift) => (
                  <MenuItem key={shift} value={shift}>
                    {shift}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Update Class'}
            </Button>
          </Grid>
        </Grid>
      </form>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </FormContainer>
  );
}

export default ManageClassForm; 